<template>
  <div class="entity-detail-peek" v-loading="loading">
    <div v-if="showClose" class="peek-top-bar">
      <el-button class="close-btn" link @click="emit('close')">
        <el-icon><Close /></el-icon>
      </el-button>
    </div>
    <el-scrollbar class="peek-scroll">
      <el-card shadow="never" class="detail-card">
        <div class="entity-detail-wrapper">
          <img src="@/assets/images/detail/detail.png" alt="" />
          <el-row class="top-title">
            <el-col :span="2"><i class="iconfont icon-entity"></i></el-col>
            <el-col :span="16">
              <h3
                v-if="titleReady"
                class="entity-title"
                :class="{ clickable: titleClickable }"
                @click="handleTitleClick"
              >
                <el-tooltip
                  effect="dark"
                  :content="name"
                  placement="top"
                  :enterable="false"
                >
                  <div class="kn-ellipsis">
                    <span class="ellipse-text">{{ name }}</span>
                  </div>
                </el-tooltip>
              </h3>
            </el-col>
          </el-row>
          <div class="tag-belong">
            <el-tag
              v-for="(typeItem, typeIndex) in typeList"
              :key="typeIndex"
              :disable-transitions="true"
            >
              {{ typeItem }}
            </el-tag>
          </div>
          <p v-if="description" class="description kn-text-align-justify">
            {{ description }}
          </p>
          <el-row
            v-for="(attributesItem, attributesIndex) in attributesList"
            :key="attributesIndex"
            class="detail-list"
          >
            <div class="detail-name">
              {{ formatAttributeDisplayName(attributesItem.name) }}
            </div>
            <div class="detail-value">{{ attributesItem.value }}</div>
          </el-row>
        </div>
      </el-card>
    </el-scrollbar>
    <div v-if="showDeleteRelation" class="peek-footer">
      <el-button type="danger" plain @click="emit('delete-relation')">
        删除与该实体的关系
      </el-button>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch } from "vue";
import { Close } from "@element-plus/icons-vue";
import { searchEntityDetail } from "@/api/graphApi";
import { formatAttributeDisplayName } from "@/utils/entityAttribute";

const props = defineProps({
  entityId: { type: String, default: "" },
  showClose: { type: Boolean, default: false },
  showDeleteRelation: { type: Boolean, default: false },
  titleClickable: { type: Boolean, default: false },
});

const emit = defineEmits(["close", "title-click", "delete-relation"]);

const loading = ref(false);
const titleReady = ref(false);
const name = ref("");
const typeList = ref([]);
const description = ref("");
const attributesList = ref([]);

async function loadDetail() {
  if (!props.entityId) {
    return;
  }
  loading.value = true;
  titleReady.value = false;
  try {
    const res = await searchEntityDetail(props.entityId);
    if (res?.code === 0 && res.data) {
      name.value = res.data.name || "";
      typeList.value = res.data.typeList || [];
      description.value = res.data.description || "";
      attributesList.value = res.data.attributesList || [];
      titleReady.value = true;
    }
  } finally {
    loading.value = false;
  }
}

function handleTitleClick() {
  if (props.titleClickable) {
    emit("title-click", props.entityId);
  }
}

watch(
  () => props.entityId,
  () => {
    loadDetail();
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.entity-detail-peek {
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;

  .peek-top-bar {
    position: relative;
    height: 20px;
    background-color: #f5f5f5;

    .close-btn {
      position: absolute;
      top: -7px;
      right: 0;
      padding: 0;
      font-size: 16px;
      color: #666;
    }
  }

  .peek-scroll {
    flex: 1;
    min-height: 0;

    .detail-card {
      background-color: #f5f5f5;
      border-radius: 0;
      border: none;
      box-shadow: none;

      :deep(.el-card__body) {
        padding: 0 15px;
      }
    }
  }

  .entity-detail-wrapper {
    .top-title {
      .el-col {
        text-align: left;
        line-height: 20px;
        margin-top: 10px;

        i {
          font-size: 20px;
        }

        h3 {
          font-size: 16px;
          margin: 0;
        }

        .entity-title {
          white-space: nowrap;

          &.clickable {
            cursor: pointer;

            &:hover {
              color: #4095e5;
            }
          }
        }
      }
    }

    .tag-belong {
      text-align: left;
      padding-top: 13px;

      .el-tag {
        height: 20px;
        padding: 0 15px;
        border: none;
        background-color: #1a73e8;
        color: #fff;
        border-radius: 10px;
        margin-right: 10px;
      }
    }

    .description {
      padding-top: 19px;
      text-align: left;
      color: #666;
      line-height: 20px;
      margin: 0;
    }

    .detail-list {
      padding-top: 20px;
      text-align: left;
      line-height: 20px;
      color: #101010;

      .detail-name {
        width: 80px;
      }

      .detail-value {
        flex: 1;
      }
    }

    img {
      width: 100%;
    }
  }

  .peek-footer {
    flex-shrink: 0;
    margin-top: auto;
    padding: 12px 16px 16px;
    text-align: right;
    background-color: #f5f5f5;
  }
}
</style>
